import React, {Component} from "react"
import moment from "moment"

import {H3} from "@irati/ui/src/Typography"
import {hangPromise} from "@irati/utils/promise"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {Deal, PerkRedemptionsReport, PaymentCurrencyType, DealStatus} from "@irati/types"
import adminTracking from "@irati-tracking/admin"
import dealsService from "@irati/deals/services/dealsGraph"
import Flex from "@irati/ui/src/Flex"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import teamsService from "@irati/identity/services/teams"
import Loading from "@irati/ui/src/Loading"
import Select from "@irati/ui/src/FormItem/Select"
import Badge from "@irati/ui/src/Badge"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import FilterChipMultiSelect from "@irati/ui/src/FormItem/FilterChipMultiSelect"

import columns from "./columns"
import {HeaderWrapper, PillWrapper} from "../../styled/styled"
import {StyledCard} from "../../../../containers/Reporting/styled"
import {Table} from "./styled"
import Download from "./Download"
import {ALL_TEAMS, LAST_30_DAYS, LAST_12_MONTHS, MONTHLY, LIMIT, Option, Filters} from "./constants"
import EmptyData from "../../EmptyData/EmptyData"

interface Props extends InjectIntl {}

interface State {
  timeRangeOptions: Option[],
  teamOptions: Option[],
  regionOptions: Option[],
  timeRange?: Date | string,
  load: Promise<void>,
  showButtons: boolean,
  showNoData: boolean,
  topPerks: Deal[],
  filters: Filters,
  showAll: boolean
}

class MostPopularPerks extends Component<Props, State> {
  state = {
    timeRangeOptions: [],
    teamOptions: [],
    regionOptions: [],
    timeRange: LAST_30_DAYS,
    load: hangPromise(),
    showButtons: false,
    showNoData: false,
    topPerks: [],
    filters: {
      businessUnitIds: [],
      teamId: ALL_TEAMS,
      month: undefined,
      currency: PaymentCurrencyType.Gbp
    },
    showAll: false
  }

  componentDidMount() {
    const {formatMessage} = this.props
    this.getBusinessRedemptionReport().then(async(availableDates) => {
      if (availableDates) {
        const businessUnits = await officesForTenant()
        const teams = await teamsService.list(authentication.business().uuid)
        const baseTeamOptions = teams.map((team) => ({label: team.teamName, value: team.uuid}))
        const teamOptions = [
          {label: formatMessage({id: "admin.reporting.popularPerks.allTeams"}), value: ALL_TEAMS},
          ...baseTeamOptions
        ]

        const regionOptions = businessUnits.map((team) => ({
          label: team.teamName.replace("platform.billingUnit.", ""),
          value: team.uuid}))

        const [, ...previousMonths] =  availableDates
        const timeRangeOptions = [
          {
            label: formatMessage({id: "admin.reporting.popularPerks.last30Days"}),
            value: LAST_30_DAYS},
          {
            label: formatMessage({id: "admin.reporting.popularPerks.last12Months"}),
            value: LAST_12_MONTHS},
          ...previousMonths.slice(0, 12).map((value) => {
            return {
              label: `${moment.months(value.month - 1)} '${value.year.toString().substring(2)}`,
              value: `${value.month}-${value.year}`
            }
          })
        ]

        this.setState({
          timeRangeOptions,
          timeRange: timeRangeOptions[0].value,
          teamOptions,
          regionOptions,
          showButtons: true})
      }
    })
  }

  buildTeams = () => {
    const {filters} = this.state
    if (filters.businessUnitIds.length) {
      return filters.businessUnitIds
    }
    if (filters.teamId !== ALL_TEAMS) {
      return [filters.teamId]
    }
  }

  getBusinessRedemptionReport = () => {
    const {timeRange, filters} = this.state

    const load = adminReportingService.getBusinessRedemptionReport({
      businessId: authentication.business().uuid,
      limit: LIMIT,
      duration: timeRange === LAST_30_DAYS || timeRange === LAST_12_MONTHS ? timeRange : MONTHLY,
      month: timeRange === LAST_30_DAYS || timeRange === LAST_12_MONTHS ? undefined : filters.month,
      teams: this.buildTeams()}, {fetchPolicy: "network-only"})
      .then((res) => {
        const perks = res.report.perks

        if (!perks.length) {
          this.setState({showNoData: true, topPerks: []})
          return res.availableDates
        }

        const uuids = perks.map((item: PerkRedemptionsReport) => {return item.uuid})
        dealsService.list({
          uuids,
          ignoreBlacklist: true,
          ignorePackage: true,
          statuses: [DealStatus.On, DealStatus.Off, DealStatus.Unavailable, DealStatus.Hidden, DealStatus.Archived],
          limit: LIMIT}, {fetchPolicy: "network-only"})
          .then((list) => {
            if (list && list.results.length > 0) {
              const topPerks:Deal[]  = list.results.map((item: Deal) => ({
                ...item,
                ...perks.find((p: PerkRedemptionsReport) => p.uuid === item.uuid)
              })).sort((a,b) => b.redemptions - a.redemptions)
              this.setState({topPerks})
            }
          })
        return res.availableDates
      })
    this.setState({load, showNoData: false})
    return load
  }

  handleSetTimeRangeUpdate = (timeRange: string) => {
    adminTracking.analyticsTopPerksSetDate()
    const time = timeRange.split("-")
    const month = timeRange ? {month: parseInt(time[0], 10), year: parseInt(time[1], 10)} : undefined

    this.setState((prevState: State) => ({
      timeRange,
      filters: {
        ...prevState.filters,
        month
      }
    }), this.getBusinessRedemptionReport)
  }

  handleUpdateBusinessUnit = (businessUnitIds: string[]) => {
    adminTracking.analyticsTopPerksSetCountry()
    this.setState((prevState: State) => ({
      filters: {
        ...prevState.filters,
        businessUnitIds
      }
    }), this.getBusinessRedemptionReport)
  }

  handleUpdateTeam = (teamId: string) => {
    adminTracking.analyticsTopPerksSetTeam()
    this.setState((prevState: State) => ({
      filters: {
        ...prevState.filters,
        teamId
      }
    }), this.getBusinessRedemptionReport)
  }

  handleUpdateCurrency = (currency: string) => {
    this.setState((prevState: State) => ({
      filters: {
        ...prevState.filters,
        currency
      }
    }), this.getBusinessRedemptionReport)
  }

  getTeamOptions = () => {
    const {formatMessage} = this.props
    const {teams} = this.state
    const options = teams.map((team) => ({label: team.teamName, value: team.uuid}))
    return [
      {label: formatMessage({id: "admin.reporting.popularPerks.csv.headers.allTeams"}), value: ALL_TEAMS},
      ...options
    ]
  }

  toggleShowAll = () => {
    adminTracking.analyticsTopPerksClickSeeAll()
    this.setState((prevState: State) => ({
      showAll: !prevState.showAll
    }))
  }

  render() {
    const {load, timeRangeOptions, timeRange, topPerks, filters, teamOptions, showButtons, showNoData, regionOptions, showAll} = this.state
    const buttonText = showAll ? "admin.reporting.popularPerks.buttons.seeLess" : "admin.reporting.popularPerks.buttons.seeAll"
    const perksToRender = showAll || topPerks.length < 5 ? topPerks : topPerks.slice(0, 4)
    const {formatMessage} = this.props

    return (
      <StyledCard>
        <Flex justifyContent="space-between" direction="column">
          <HeaderWrapper justifyContent="flex-start">
            <H3>
              <FormattedMessage id="admin.reporting.popularPerks.title" />
            </H3>
            {!!topPerks.length && <PillWrapper>
              <Badge>{topPerks.length}</Badge>
            </PillWrapper>}
          </HeaderWrapper>
          {showButtons &&
            <Flex alignSelf="flex-end">
              <FilterChipMultiSelect
                isDisabled={filters.teamId !== ALL_TEAMS}
                onChange={this.handleUpdateBusinessUnit}
                defaultLabel="admin.reporting.countriesMultiSelect.defaultLabel"
                selectedLabel="admin.reporting.countriesMultiSelect.selectedLabel"
                icon={IconTypes.countries}
                options={regionOptions}
                selectedHeader="admin.reporting.countriesMultiSelect.selectedHeader"
                unselectedHeader="admin.reporting.countriesMultiSelect.unselectedHeader"
                nothingSelectedHeader="admin.reporting.countriesMultiSelect.nothingSelectedHeader" />
              <Select
                id="adminSection_topRedeemedPerks_teams"
                isClearable={false}
                value={filters.teamId}
                options={teamOptions}
                onChange={this.handleUpdateTeam}
                isDisabled={filters.businessUnitIds?.length} />
              <Select
                id="adminSection_topRedeemedPerks_timePeriod"
                name="timeRangeMostPopularPerks"
                options={timeRangeOptions}
                value={timeRange}
                onChange={this.handleSetTimeRangeUpdate} />
              <Select
                isClearable={false}
                value={filters.currency}
                options={[
                  {value: PaymentCurrencyType.Gbp, label: PaymentCurrencyType.Gbp},
                  {value: PaymentCurrencyType.Aud, label: PaymentCurrencyType.Aud}
                ]}
                onChange={this.handleUpdateCurrency} />
            </Flex>
          }
        </Flex>
        <Loading load={load}>
          {topPerks?.length > 0 &&
            <>
              <Table
                manual={false}
                showPagination={false}
                data={perksToRender}
                columns={columns(formatMessage, filters.currency)} />
              <Flex justifyContent="center">
                <Button primary onClick={this.toggleShowAll}>
                  <FormattedMessage id={buttonText} />
                  <Icon icon={showAll ? IconTypes.chevronUp : IconTypes.chevronDown} marginLeft />
                </Button>
              </Flex>

            </>
          }
          {showNoData && <EmptyData />}
        </Loading>
        <Download
          timeRange={timeRange}
          timeRangeOptions={timeRangeOptions}
          filters={filters}
          regionOptions={regionOptions}
          teamOptions={teamOptions}
          showButtons={showButtons} />
      </StyledCard>
    )
  }
}

export default injectIntl(MostPopularPerks)
